import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfraNav from './components/InfraNav';
import InfraFooter from './components/InfraFooter';
import InfraCTA from './components/InfraCTA';
import './infrastructure.css';

/* ── PAYE Tax Calculation (Nigerian NTA 2025 bands) ── */
function calcPAYE(annualChargeable) {
  if (annualChargeable <= 0) return 0;
  const bands = [
    { limit: 800000, rate: 0 },
    { limit: 2200000, rate: 0.15 },
    { limit: 9000000, rate: 0.18 },
    { limit: 13000000, rate: 0.21 },
    { limit: 25000000, rate: 0.23 },
    { limit: Infinity, rate: 0.25 },
  ];
  let remaining = annualChargeable;
  let tax = 0;
  for (const band of bands) {
    const taxable = Math.min(remaining, band.limit);
    tax += taxable * band.rate;
    remaining -= taxable;
    if (remaining <= 0) break;
  }
  return tax;
}

function LivePayrollCalculator() {
  const [totalPackage, setTotalPackage] = useState(500000);
  const DEFAULTS = { meals: 30000, transport: 20000, health: 25000, insurance: 10000 };
  const [benefits, setBenefits] = useState({ ...DEFAULTS });
  const scaleRef = useRef(500000);

  const handlePackageChange = useCallback((val) => {
    const clamped = Math.max(100000, Math.min(5000000, val));
    const ratio = clamped / scaleRef.current;
    if (Math.abs(ratio - 1) > 0.001) {
      setBenefits(prev => ({
        meals: Math.round(prev.meals * ratio / 1000) * 1000,
        transport: Math.round(prev.transport * ratio / 1000) * 1000,
        health: Math.round(prev.health * ratio / 1000) * 1000,
        insurance: Math.round(prev.insurance * ratio / 1000) * 1000,
      }));
      scaleRef.current = clamped;
    }
    setTotalPackage(clamped);
  }, []);

  const breakdown = useMemo(() => {
    const totalBen = benefits.meals + benefits.transport + benefits.health + benefits.insurance;
    const basicPct = 0.35, housingPct = 0.15, transportPct = 0.10;
    const pctSum = basicPct + housingPct + transportPct;
    const employerPensionRate = 0.10, employeePensionRate = 0.08;
    const cashSalary = (totalPackage - totalBen) / (1 + employerPensionRate * pctSum);
    const pensionable = pctSum * cashSalary;
    const employerPension = employerPensionRate * pensionable;
    const employeePension = employeePensionRate * pensionable;
    const annualCash = cashSalary * 12;
    const lifeDeduction = benefits.insurance * 12;
    const chargeable = annualCash - employeePension * 12 - lifeDeduction;
    const annualPAYE = calcPAYE(chargeable);
    const monthlyPAYE = annualPAYE / 12;
    const netToBank = cashSalary - monthlyPAYE - employeePension;
    return { netToBank, employeePension, employerPension, monthlyPAYE, totalBen };
  }, [totalPackage, benefits]);

  const fmt = (n) => `₦${Math.round(Math.max(0, n)).toLocaleString()}`;

  const items = [
    { label: 'Your bank account', amount: breakdown.netToBank, color: '#00B894', highlight: true, sub: null },
    { label: 'Meals', amount: benefits.meals, color: '#f59e0b', sub: 'via company vendor' },
    { label: 'Transport', amount: benefits.transport, color: '#ec4899', sub: 'via company arrangement' },
    { label: 'Health cover', amount: benefits.health, color: '#00B894', sub: 'HMO provider' },
    { label: 'Life insurance', amount: benefits.insurance, color: '#8b5cf6', sub: 'insurance provider' },
    { label: 'Your pension fund', amount: breakdown.employeePension + breakdown.employerPension, color: '#6366f1', sub: 'employee 8% + employer 10%' },
    { label: 'PAYE tax', amount: breakdown.monthlyPAYE, color: '#6b7280', sub: null },
  ];

  const total = items.reduce((s, i) => s + i.amount, 0);

  return (
    <div style={{ background: '#252729', borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)', padding: 24, boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }} data-testid="live-payroll-calculator">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#fff', margin: 0 }}>Where Your {fmt(totalPackage)} Goes</h3>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 99, background: 'rgba(0,184,148,0.15)', color: '#00B894' }}>Live</span>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '12px 16px', marginBottom: 8 }}>
        <p style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 4 }}>Pay & Benefits Package</p>
        <input
          type="text"
          inputMode="numeric"
          value={`₦${totalPackage.toLocaleString()}`}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, '');
            const v = raw === '' ? 100000 : parseInt(raw, 10);
            scaleRef.current = totalPackage;
            handlePackageChange(v);
          }}
          style={{ width: '100%', background: 'transparent', fontSize: 24, fontWeight: 800, color: '#fff', border: 'none', outline: 'none', fontFamily: 'monospace' }}
          data-testid="calc-package-input"
        />
      </div>

      <div style={{ marginBottom: 20, padding: '0 4px' }}>
        <input
          type="range" min={100000} max={5000000} step={10000} value={totalPackage}
          onChange={(e) => { scaleRef.current = totalPackage; handlePackageChange(parseInt(e.target.value)); }}
          style={{ width: '100%', height: 6, borderRadius: 3, appearance: 'none', cursor: 'pointer', background: `linear-gradient(to right, #D64D7A ${((totalPackage - 100000) / 4900000) * 100}%, rgba(255,255,255,0.1) ${((totalPackage - 100000) / 4900000) * 100}%)` }}
          data-testid="calc-package-slider"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontSize: 10, color: '#6b7280' }}>₦100K</span>
          <span style={{ fontSize: 10, color: '#6b7280' }}>₦5M</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 16 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.highlight ? '#00B894' : 'rgba(255,255,255,0.2)' }} />
              {i < items.length - 1 && <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.1)' }} />}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1, padding: '6px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${item.color}20` }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                </div>
                <div>
                  <span style={{ fontSize: 13, fontWeight: item.highlight ? 700 : 400, color: item.highlight ? '#fff' : '#d1d5db' }}>{item.label}</span>
                  {item.sub && <p style={{ fontSize: 10, color: '#6b7280', margin: 0 }}>{item.sub}</p>}
                </div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: item.highlight ? '#00B894' : '#fff', fontFamily: 'monospace' }}>{fmt(item.amount)}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 4px 0' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>Every naira accounted for.</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', fontFamily: 'monospace' }}>{fmt(total)}</span>
      </div>
    </div>
  );
}

export default function InfrastructurePayroll() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="infra-page" data-testid="infrastructure-payroll">
      <InfraNav transparent={false} />

      {/* ══════ HERO ══════ */}
      <section style={{ paddingTop: 24 }}>
        <div style={{ paddingTop: 48, paddingBottom: 64 }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} className="infra-payroll-hero-grid">
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 99, background: 'rgba(214,77,122,0.1)', color: '#D64D7A', fontSize: 14, fontWeight: 500, marginBottom: 24 }}>
                  Your pay and benefits deserve one platform
                </div>
                <h1 style={{ fontSize: 44, fontWeight: 800, color: '#1a1d1f', lineHeight: 1.1, letterSpacing: '-0.5px', marginBottom: 24, fontFamily: 'Manrope, sans-serif' }} data-testid="payroll-hero-headline">
                  Your pay and benefits isn't one big rock.{' '}
                  <span style={{ color: '#D64D7A' }}>It's pebbles.</span>
                </h1>
                <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.7, maxWidth: 480, marginBottom: 32 }}>
                  Salary is your pay. Food, health cover, transport, pension: those are benefits your company provides. Pebbles brings them together. Delivered, tracked, and compliant.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Link to="/login" className="infra-btn-primary" data-testid="payroll-get-started">Get Started</Link>
                  <Link to="/infrastructure" className="infra-btn-secondary" data-testid="payroll-back-link">All Products</Link>
                </div>
              </div>

              <div style={{ position: 'relative' }} className="infra-payroll-hero-image">
                <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.1)' }}>
                  <img
                    src="https://images.pexels.com/photos/8547344/pexels-photo-8547344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Team collaborating"
                    style={{ width: '100%', height: 400, objectFit: 'cover', display: 'block' }}
                    data-testid="payroll-hero-image"
                  />
                </div>
                <div style={{ position: 'absolute', bottom: -24, left: -16, background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', border: '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Avg. Monthly Benefits Delivered</p>
                  <p style={{ fontSize: 28, fontWeight: 800, color: '#00B894', fontFamily: 'Manrope, sans-serif', margin: 0 }}>₦85,000</p>
                  <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Per employee, through vendors and providers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ COMPENSATION FLOW STRIP ══════ */}
      <section style={{ padding: '32px 0', background: '#f9fafb', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#1a1d1f', textAlign: 'center', marginBottom: 24, fontFamily: 'Manrope, sans-serif' }}>
            Payroll that doesn't just pay. It delivers a full compensation package.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: '#e5e7eb', borderRadius: 12, overflow: 'hidden' }} className="infra-payroll-strip-grid">
            {[
              'Your company\'s total pay and benefits package flows in. Pebbles handles everything: cash to wallets, food benefits to vendors, premiums to HMO providers.',
              'Contributions to pension, transport benefits to approved providers. Every benefit is delivered, every naira is tracked.',
              'Automatically, every cycle. One payroll run delivers cash salary and company-provided benefits together.',
            ].map((text, i) => (
              <div key={i} style={{ background: '#fff', padding: 24, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(214,77,122,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#D64D7A', opacity: 0.6 }} />
                </div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ STATS BAR ══════ */}
      <section style={{ padding: '40px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32, textAlign: 'center' }} className="infra-payroll-stats-grid">
            {[
              { val: '₦85K', label: 'Avg. Monthly Benefits Delivered' },
              { val: '70%', label: 'Employee Enrollment Rate' },
              { val: '100%', label: 'Fully Compliant Platform' },
              { val: '500+', label: 'Companies Trust Us' },
            ].map((s, i) => (
              <div key={i}>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#1a1d1f', margin: 0, fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
                <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ BENEFITS GRID ══════ */}
      <section style={{ padding: '64px 0', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 48px' }}>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D64D7A', marginBottom: 12 }}>Real Benefits, Not Just a Payslip</p>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: '#1a1d1f', letterSpacing: '-0.3px', fontFamily: 'Manrope, sans-serif', margin: 0 }}>
              When your company provides food, transport, and health cover, those are benefits, not salary
            </h2>
            <p style={{ fontSize: 16, color: '#6b7280', marginTop: 16 }}>
              Under Nigerian law, company-provided benefits are treated differently from cash income. Pebbles makes it easy for companies to structure and deliver these benefits properly.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="infra-payroll-features-grid">
            {[
              { title: 'Food Benefits', desc: 'Company-provided food benefits delivered to approved vendors. A benefit from your employer, not a salary deduction.' },
              { title: 'Health Cover (HMO)', desc: 'Choose from premium HMO providers. Premiums are paid by your company as part of your compensation package.' },
              { title: 'Pension Contributions', desc: 'Company pension contributions routed directly to your PFA. Building your future as a proper employer benefit.' },
              { title: 'Transport Benefits', desc: 'Transport provided as a proper employer benefit. Delivered through approved channels, tracked and compliant.' },
              { title: 'Life Insurance', desc: 'Company-provided life insurance for you and your dependents. A benefit that protects your family.' },
              { title: 'Full Compensation Visibility', desc: 'See your total compensation in one wallet: cash, food, transport, HMO, pension. The full picture.' },
            ].map((f, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', padding: 24, transition: 'box-shadow 0.3s, border-color 0.3s' }} data-testid={`payroll-feature-card-${i}`}>
                <div style={{ width: 44, height: 44, borderRadius: 8, background: 'rgba(214,77,122,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#D64D7A', opacity: 0.5 }} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a1d1f', marginBottom: 6, fontFamily: 'Manrope, sans-serif' }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ DARK SECTION: HOW COMPENSATION FLOWS ══════ */}
      <section style={{ background: '#1a1d1f', padding: '80px 0', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center' }} className="infra-payroll-dark-grid">
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 99, background: 'rgba(255,255,255,0.1)', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#fff', marginBottom: 24 }}>
                One Payroll Run
              </div>
              <h2 style={{ fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.3px', marginBottom: 20, fontFamily: 'Manrope, sans-serif' }}>
                One payroll run. Salary plus benefits,{' '}
                <span style={{ color: '#D64D7A' }}>delivered.</span>
              </h2>
              <p style={{ color: '#9ca3af', lineHeight: 1.7, marginBottom: 32, maxWidth: 480 }}>
                Your company deposits total compensation once. Pebbles delivers it: cash salary to wallets, food benefits to vendors, premiums to HMO, contributions to pension. Employees see their full compensation package in real time.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
                {[
                  'Cash salary delivered to employee wallets',
                  'Food benefits routed to approved vendors',
                  'HMO premiums paid to chosen providers',
                  'Pension contributions sent to PFAs',
                  'Transport benefits delivered through approved channels',
                  'Payslips and compliance reports generated automatically',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'rgba(0,184,148,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 9, color: '#00B894' }}>✓</span>
                    </div>
                    <span style={{ fontSize: 14, color: '#d1d5db' }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/login" className="infra-btn-primary" style={{ boxShadow: '0 2px 12px rgba(214,77,122,0.4)' }} data-testid="payroll-dark-get-started">
                Get Started
              </Link>
            </div>

            <LivePayrollCalculator />
          </div>
        </div>
      </section>

      {/* ══════ HOW IT WORKS ══════ */}
      <section style={{ padding: '80px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }} className="infra-payroll-hiw-grid">
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D64D7A', marginBottom: 12 }}>Simple Setup</p>
              <h2 style={{ fontSize: 30, fontWeight: 800, color: '#1a1d1f', letterSpacing: '-0.3px', marginBottom: 20, fontFamily: 'Manrope, sans-serif' }}>
                Up and running in minutes, not weeks
              </h2>
              <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 40 }}>
                No complex onboarding. Upload your team, let them choose their benefits, and run your first payroll in one afternoon.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                {[
                  { num: '01', title: 'Add Your People', desc: 'Upload your staff schedule as a CSV. Each employee gets a wallet and bank account within minutes.' },
                  { num: '02', title: 'Let Them Choose', desc: 'Employees log in to select their benefits: HMO provider, pension contribution level, food and transport preferences.' },
                  { num: '03', title: 'Run Payroll Once', desc: 'Deposit total compensation. Pebbles delivers cash to wallets and benefits to the right providers and vendors.' },
                  { num: '04', title: 'Stay in Control', desc: 'Every naira is tracked. Dashboards, payslips, audit logs, and compliance reports update in real time.' },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 8, background: '#D64D7A', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(214,77,122,0.3)' }}>
                      <span style={{ color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>{s.num}</span>
                    </div>
                    <div>
                      <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1a1d1f', marginBottom: 2, fontFamily: 'Manrope, sans-serif' }}>{s.title}</h4>
                      <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ position: 'relative' }} className="infra-payroll-hiw-image">
              <div style={{ borderRadius: 16, overflow: 'hidden' }}>
                <img
                  src="https://images.unsplash.com/photo-1573167101669-476636b96cea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwdGVhbSUyMGJ1c2luZXNzJTIwbWVldGluZyUyMG9mZmljZSUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzcyMDQ0NjE1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Team collaboration"
                  style={{ width: '100%', height: 440, objectFit: 'cover', display: 'block' }}
                />
              </div>
              <div style={{ position: 'absolute', bottom: -20, right: -16, background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', border: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,184,148,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 16, color: '#00B894' }}>&#10003;</span>
                  </div>
                  <div>
                    <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>Employees Onboarded</p>
                    <p style={{ fontSize: 18, fontWeight: 800, color: '#1a1d1f', margin: 0, fontFamily: 'Manrope, sans-serif' }}>45,000+</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ FOR EMPLOYERS / EMPLOYEES ══════ */}
      <section style={{ padding: '80px 0', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 48px' }}>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D64D7A', marginBottom: 12 }}>Built For Everyone</p>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: '#1a1d1f', letterSpacing: '-0.3px', fontFamily: 'Manrope, sans-serif', margin: 0 }}>Modern benefits for modern teams</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }} className="infra-payroll-audience-grid">
            <div style={{ background: '#1a1d1f', borderRadius: 12, padding: 40, color: '#fff' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 99, background: 'rgba(255,255,255,0.1)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 20 }}>
                For Employers
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, fontFamily: 'Manrope, sans-serif' }}>Same payroll spend. More in your employee's pocket every month.</h3>
              <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
                Provide real benefits: meals, transport, health cover, delivered through vendors. One payroll run. Full compliance.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['One payroll run delivers cash and benefits', 'Bulk employee onboarding via CSV', 'NRS and state revenue compliant', 'Complete audit trail and reconciliation'].map((t, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#d1d5db' }}>
                    <span style={{ color: '#00B894', flexShrink: 0 }}>&#10003;</span> {t}
                  </li>
                ))}
              </ul>
              <Link to="/infrastructure" style={{ display: 'inline-block', background: '#fff', color: '#1a1d1f', borderRadius: 8, fontSize: 14, fontWeight: 600, padding: '10px 20px', textDecoration: 'none' }}>
                See How It Works &rarr;
              </Link>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(214,77,122,0.1)', padding: 40 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 99, background: 'rgba(214,77,122,0.1)', color: '#D64D7A', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 20 }}>
                For Employees
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1d1f', marginBottom: 12, fontFamily: 'Manrope, sans-serif' }}>See your full compensation, not just your salary</h3>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
                Your wallet shows everything: cash, food benefits, transport, HMO, pension. Choose your providers, track every benefit delivered.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['Full compensation visibility in one wallet', 'Choose your own HMO and benefit providers', 'Track every benefit delivered in real time', 'See your complete compensation package'].map((t, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#374151' }}>
                    <span style={{ color: '#D64D7A', flexShrink: 0 }}>&#10003;</span> {t}
                  </li>
                ))}
              </ul>
              <Link to="/login" className="infra-btn-primary" data-testid="payroll-employee-cta">
                Sign Up Free &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ RESOURCES ══════ */}
      <section style={{ padding: '80px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 48px' }}>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D64D7A', marginBottom: 12 }}>Resources</p>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: '#1a1d1f', letterSpacing: '-0.3px', fontFamily: 'Manrope, sans-serif', margin: 0 }}>Resources to help you get started</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }} className="infra-payroll-resources-grid">
            {[
              { img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=340&fit=crop', tag: 'Benefits Structuring', title: 'How Company-Provided Benefits Work Under Nigerian Tax Law' },
              { img: 'https://images.unsplash.com/photo-1573164574511-73c773193279?w=600&h=340&fit=crop', tag: 'HR and Benefits', title: 'The Main Types of Employee Benefits HR Should Know' },
              { img: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&h=340&fit=crop', tag: 'Compliance', title: 'NRS Compliance: Delivering Employee Benefits Properly' },
            ].map((r, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.3s' }} data-testid={`payroll-resource-card-${i}`}>
                <div style={{ height: 176, overflow: 'hidden' }}>
                  <img src={r.img} alt={r.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s' }} />
                </div>
                <div style={{ padding: 20 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#D64D7A', marginBottom: 8 }}>{r.tag}</p>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a1d1f', lineHeight: 1.4, margin: 0, fontFamily: 'Manrope, sans-serif' }}>{r.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <InfraCTA />
      <InfraFooter />

      {/* Responsive overrides */}
      <style>{`
        @media (max-width: 1024px) {
          .infra-payroll-hero-image { display: none; }
          .infra-payroll-hero-grid { grid-template-columns: 1fr !important; }
          .infra-payroll-dark-grid { grid-template-columns: 1fr !important; }
          .infra-payroll-hiw-grid { grid-template-columns: 1fr !important; }
          .infra-payroll-hiw-image { display: none; }
        }
        @media (max-width: 768px) {
          .infra-payroll-strip-grid { grid-template-columns: 1fr !important; }
          .infra-payroll-stats-grid { grid-template-columns: 1fr 1fr !important; }
          .infra-payroll-features-grid { grid-template-columns: 1fr !important; }
          .infra-payroll-audience-grid { grid-template-columns: 1fr !important; }
          .infra-payroll-resources-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
